import type { User } from '../types'

/**
 * 与后端 wecom_sync_enabled 的展示语义对齐：仅显式 false 为关闭，缺省/undefined 视为开启。
 */
export function normalizeSyncWecom(v: unknown): boolean {
  return v !== false
}

/**
 * 登录接口已带齐资料；GET /wechat/user 应以库为准，但若后者出现空串/null/缺字段，
 * 不应覆盖登录响应里仍有效的手机、经销商等（避免换设备后界面被「清空」）。
 */
export function mergeLoginUserWithServerProfile(
  loginUser: User,
  profile: Partial<User> & { department_name?: string | null } | null | undefined
): User {
  if (!profile) {
    return {
      ...loginUser,
      is_sync_wecom: normalizeSyncWecom(loginUser.is_sync_wecom),
    }
  }

  const phoneFrom = (v: string | undefined) => (v ?? '').trim()
  const mergedPhone =
    phoneFrom(profile.consultant_phone) || phoneFrom(loginUser.consultant_phone)

  const mergedName =
    (profile.consultant_name && profile.consultant_name.trim()) ||
    loginUser.consultant_name ||
    ''

  const mergedDealer =
    profile.dealer_id !== undefined && profile.dealer_id !== null
      ? profile.dealer_id
      : loginUser.dealer_id

  const mergedDept =
    profile.department_name !== undefined && profile.department_name !== null
      ? profile.department_name
      : (loginUser as User & { department_name?: string | null }).department_name

  return {
    ...loginUser,
    ...profile,
    consultant_name: mergedName,
    consultant_phone: mergedPhone,
    dealer_id: mergedDealer,
    department_name: mergedDept,
    avatar_url:
      (profile.avatar_url && profile.avatar_url.trim()) ||
      loginUser.avatar_url ||
      '',
    is_sync_wecom: normalizeSyncWecom(profile.is_sync_wecom),
  } as User
}

/**
 * 冷启动/恢复会话时：以服务端库表为准（GET /wechat/user），避免本地残留旧对象与数据库不一致；
 * 头像若本次未生成签名 URL，可暂沿用本地已有签名链接。
 */
export function hydrateUserFromServer(
  local: User | null,
  server: User & { department_name?: string | null }
): User {
  return {
    ...server,
    avatar_url:
      (server.avatar_url && server.avatar_url.trim()) ||
      local?.avatar_url ||
      '',
    is_sync_wecom: normalizeSyncWecom(server.is_sync_wecom),
  } as User
}
